import * as FC from "@fluentui/react-components";
import * as FCU from "@fluentui/react-components/unstable";
import * as FDP from "@fluentui/react-datepicker-compat";
import * as IC from "@fluentui/react-icons";
import { useContext, useEffect, useState, useCallback } from "react";
import { toDateString } from "../../date_utils";
import { idString } from "../../db_utils";
import { apiClient } from "../../services/api";
import { Context } from "../../context";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.padding("10px"),
        ...FC.shorthands.gap("10px"),
    },
    detail: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        ...FC.shorthands.gap("10px"),
    },
    nowrap: {
        whiteSpace: "nowrap",
    },
});

export const FieldDetail = ({ field, onFieldChange, tenantId }) => {
    let [fieldInfo, setFieldInfo] = useState({});
    let [assetOwners, setAssetOwners] = useState([]);

    let styles = useStyles();

    useEffect(() => {
        apiClient.getAssetOwners({ status: "active" })
            .then((items) => setAssetOwners(items || []))
            .catch((error) => console.error("Failed to load asset owners:", error));
    }, []);

    const updateInfo = (key) => {
        return (evt) => {
            // Convert to PascalCase for backend
            let capitalKey;
            if (key === 'contact_phone') {
                capitalKey = 'ContactPhone';
            } else {
                capitalKey = key.charAt(0).toUpperCase() + key.slice(1);
            }
            let kv = {};
            kv[capitalKey] = evt.target.value;
            setFieldInfo({ ...fieldInfo, ...kv });
        };
    };

    const updatePowerInfo = (evt) => {
        const value = parseFloat(evt.target.value) || undefined;
        setFieldInfo({ ...fieldInfo, Power: value });
    };

    const updateLongitude = (evt) => {
        const value = parseFloat(evt.target.value) || undefined;
        setFieldInfo({ ...fieldInfo, Longitude: value });
    };

    const updateLatitude = (evt) => {
        const value = parseFloat(evt.target.value) || undefined;
        setFieldInfo({ ...fieldInfo, Latitude: value });
    };

    const doSave = async () => {
        try {
            if (!tenantId) {
                alert("A target tenant is required before changing a field.");
                return;
            }
            if (field) {
                // Update existing field
                fieldInfo.UpdatedAt = new Date();
                await apiClient.updateFieldItem(field.id, fieldInfo, tenantId);
                onFieldChange();
            } else {
                // Create new field
                console.log("Creating field with data:", fieldInfo);
                await apiClient.createFieldItem(fieldInfo, tenantId);
                onFieldChange();
            }
        } catch (error) {
            console.error("Failed to save field item:", error);
            console.error("Field data was:", fieldInfo);
            alert("保存失败: " + error.message);
        }
    };

    const doDelete = async () => {
        try {
            if (!tenantId) {
                alert("A target tenant is required before changing a field.");
                return;
            }
            await apiClient.deleteFieldItem(field.id, tenantId);
            onFieldChange();
        } catch (error) {
            console.error("Failed to delete field item:", error);
            alert("删除失败: " + error.message);
        }
    };

    return (
        <div className={styles.detail}>
            <FC.Divider />
            <FC.Input
                contentBefore={"风场编号"}
                defaultValue={field?.seq || ""}
                onChange={updateInfo("seq")}
            />
            <FC.Input
                contentBefore={"风场名称"}
                defaultValue={field?.name || ""}
                onChange={updateInfo("name")}
            />
            <FC.Label>业主</FC.Label>
            <FC.Dropdown
                defaultSelectedOptions={
                    (fieldInfo.AssetOwnerId && [fieldInfo.AssetOwnerId.toString()]) ||
                    (field?.assetOwnerId && [field.assetOwnerId.toString()]) ||
                    (assetOwners[0]?.id && [assetOwners[0].id.toString()]) ||
                    []
                }
                defaultValue={
                    assetOwners.find((owner) => owner.id === (field?.assetOwnerId || assetOwners[0]?.id))?.name ||
                    "选择业主"
                }
                onOptionSelect={(_, data) => {
                    setFieldInfo({ ...fieldInfo, AssetOwnerId: Number(data.optionValue) });
                }}
            >
                {assetOwners.map((owner) => (
                    <FC.Option key={owner.id} value={owner.id?.toString()}>
                        {`${owner.seq} - ${owner.name}`}
                    </FC.Option>
                ))}
            </FC.Dropdown>
            <FC.Input
                type="number"
                contentBefore={"装机容量"}
                defaultValue={field?.power}
                contentAfter={"MW"}
                onChange={updatePowerInfo}
            />
            <FC.Input
                contentBefore={"风场地址"}
                defaultValue={field?.address || ""}
                onChange={updateInfo("address")}
            />
            <FC.Label>风场类型</FC.Label>
            <FC.Dropdown
                defaultValue={field?.type}
                defaultSelectedOptions={field?.type ? [field.type] : []}
                onOptionSelect={(_, data) => {
                    setFieldInfo({ ...fieldInfo, Type: data.optionText });
                }}
            >
                <FC.Option>海上</FC.Option>
                <FC.Option>陆地</FC.Option>
                <FC.Option>其他</FC.Option>
            </FC.Dropdown>
            <FC.Label>建设日期</FC.Label>
            <FDP.DatePicker
                defaultValue={
                    field?.built_at ? new Date(field.built_at) : undefined
                }
                formatDate={toDateString}
                onSelectDate={(date) => {
                    console.log("date---", date);
                    setFieldInfo({ ...fieldInfo, BuiltAt: date });
                }}
            />
            <FC.Input
                contentBefore={"经度"}
                defaultValue={field?.longitude}
	        type="number"
	        onChange={updateLongitude}
            />
            <FC.Input
                contentBefore={"纬度"}
	        type="number"
                defaultValue={field?.latitude || ""}
                onChange={updateLatitude}
            />
            <FC.Input
                contentBefore={"联系人"}
                defaultValue={field?.contact || ""}
                onChange={updateInfo("contact")}
            />
            <FC.Input
                contentBefore={"电话"}
                defaultValue={field?.contact_phone || ""}
                onChange={updateInfo("contact_phone")}
            />
            <FC.Label>备注</FC.Label>
            <FC.Textarea
                defaultValue={field?.description || ""}
                onChange={updateInfo("description")}
            />
            <FC.Divider />
            {field && (
                <FC.Popover withArrow>
                    <FC.PopoverTrigger>
                        <FC.Button>删除</FC.Button>
                    </FC.PopoverTrigger>
                    <FC.PopoverSurface>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "row",
                                gap: 10,
                            }}
                        >
                            <div>{`是否删除风场: ${field.seq}?`}</div>
                            <FC.Button
                                onClick={doDelete}
                                appearance="primary"
                                icon={<IC.DeleteRegular />}
                            />
                        </div>
                    </FC.PopoverSurface>
                </FC.Popover>
            )}
            <FC.Button
                appearance="primary"
                onClick={doSave}
                disabled={!fieldInfo}
            >
                {field ? "保存" : "创建"}
            </FC.Button>
        </div>
    );
};

export const FieldManagement = ({ tenantId = null }) => {
    const { user } = useContext(Context);
    const candidateTenantId = tenantId ?? user?.tenantId;
    const parsedTenantId = Number(candidateTenantId);
    const targetTenantId = Number.isInteger(parsedTenantId) && parsedTenantId > 0
        ? parsedTenantId
        : null;
    let [filter, setFilter] = useState(null);
    let [panelOpen, setPanelOpen] = useState(false);
    let [field, setField] = useState(null);
    let [fields, setFields] = useState([]);

    const onCreateClick = () => {
        if (!targetTenantId) {
            alert("A target tenant is required before creating a field.");
            return;
        }
        setField(null);
        setPanelOpen(true);
    };

    const loadFields = useCallback(async () => {
        try {
            const items = await apiClient.getFieldItems(
                targetTenantId ? { tenantId: targetTenantId } : {},
            );
            
            // Apply client-side filtering if needed
            if (filter && filter.trim()) {
                const searchable = ["seq", "name"];
                const filterLower = filter.toLowerCase();
                const filteredItems = items.filter(item => 
                    searchable.some(column => 
                        item[column]?.toString().toLowerCase().includes(filterLower)
                    )
                );
                setFields(filteredItems);
            } else {
                setFields(items);
            }
        } catch (error) {
            console.error("Failed to load field items:", error);
            setFields([]);
        }
    }, [filter, targetTenantId]);

    const onFieldChange = () => {
        loadFields();
        setPanelOpen(false);
    };

    useEffect(() => {
        loadFields();
    }, [loadFields]);

    let styles = useStyles();
    return (
        <div className={styles.root}>
            {!targetTenantId && (
                <FC.MessageBar intent="warning">
                    <FC.MessageBarBody>
                        This legacy page has no explicit target tenant. Open it from the managed
                        tenant workbench before creating, updating or archiving fields.
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center",
                }}
            >
                <FC.Label>快速检索</FC.Label>
                <FC.Input
                    style={{ flex: 1 }}
                    contentBefore={<IC.SearchRegular />}
                    placeholder="按风场名称搜索..."
                    onChange={(evt) => {
                        setFilter(evt.target.value);
                    }}
                />
                <FC.Button appearance="primary" onClick={onCreateClick}>
                    创建风场
                </FC.Button>
            </div>
            <FC.DataGrid
                getRowId={(item) => idString(item.id)}
                items={fields}
                columns={[
                    FC.createTableColumn({
                        columnId: "seq",
                        renderHeaderCell: () => {
                            return "风场编号";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {item.seq}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "name",
                        renderHeaderCell: () => {
                            return "风场名称";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {item.name}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "typ",
                        renderHeaderCell: () => {
                            return "风场类型";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {item.typ}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "power",
                        renderHeaderCell: () => {
                            return "装机容量(MW)";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {item.power}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "address",
                        renderHeaderCell: () => {
                            return "地址";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {item.address}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "contact",
                        renderHeaderCell: () => {
                            return "联系人";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {item.contact}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "contact_phone",
                        renderHeaderCell: () => {
                            return "联系电话";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {item.contact_phone}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "built_at",
                        renderHeaderCell: () => {
                            return "修建于";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {new Date(
                                        item.built_at,
                                    ).toLocaleDateString()}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        columnId: "updated_at",
                        renderHeaderCell: () => {
                            return "更新于";
                        },
                        renderCell: (item) => {
                            return (
                                <FC.TableCellLayout>
                                    {new Date(item.updated_at).toLocaleString()}
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                    FC.createTableColumn({
                        // columnId: "updated_at",
                        renderHeaderCell: () => {
                            return "操作";
                        },
                        renderCell: (item) => {
                            const onClick = () => {
                                setField(item);
                                setPanelOpen(true);
                            };
                            return (
                                <FC.TableCellLayout>
                                    <FC.Button
                                        onClick={onClick}
                                        appearance="subtle"
                                        icon={<IC.MoreCircleFilled />}
                                    />
                                </FC.TableCellLayout>
                            );
                        },
                    }),
                ]}
            >
                <FC.DataGridHeader>
                    <FC.DataGridRow>
                        {({ renderHeaderCell }, i) => (
                            <FC.DataGridHeaderCell key={i}>
                                {renderHeaderCell()}
                            </FC.DataGridHeaderCell>
                        )}
                    </FC.DataGridRow>
                </FC.DataGridHeader>
                <FC.DataGridBody>
                    {({ item }) => (
                        <FC.DataGridRow key={item.id}>
                            {({ renderCell }) => (
                                <FC.DataGridCell>
                                    {renderCell(item)}
                                </FC.DataGridCell>
                            )}
                        </FC.DataGridRow>
                    )}
                </FC.DataGridBody>
            </FC.DataGrid>
            <FCU.DrawerOverlay
                position="right"
                open={panelOpen}
                onOpenChange={(_, { open }) => setPanelOpen(open)}
                style={{ width: 500 }}
            >
                <FCU.DrawerHeader>
                    <FCU.DrawerHeaderTitle
                        action={
                            <FC.Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<IC.Dismiss24Regular />}
                                onClick={() => setPanelOpen(false)}
                            />
                        }
                    >
                        {field ? "风场详情" : "创建风场"}
                    </FCU.DrawerHeaderTitle>
                </FCU.DrawerHeader>
                <FCU.DrawerBody>
                    <FieldDetail field={field} onFieldChange={onFieldChange} tenantId={targetTenantId} />
                </FCU.DrawerBody>
            </FCU.DrawerOverlay>
            {/* <F8.Panel
                isOpen={panelOpen}
                onDismiss={() => {
                    setPanelOpen(false);
                }}
                headerText={field ? "风场详情" : "创建风场"}
            >
                <F8.ThemeProvider>
                    <FieldDetail field={field} />
                </F8.ThemeProvider>
            </F8.Panel> */}
        </div>
    );
};
